/**
 * QuickFilters Component
 *
 * Two-row filter system for search dropdown:
 * - Row 1: Display mode (Watchlist, Active, Archive) - single select
 * - Row 2: Entity type filter (CEX, DEX, SOL, Arbitrage, Balancer) - multi-select
 *   - CEX/DEX have sub-dropdowns for specific exchange selection
 */

import {
  CheckCircleOutlined,
  DownOutlined,
  FolderOutlined,
  StarFilled,
  StockOutlined,
  SwapOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Dropdown } from 'antd';
import cn from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Checkbox } from 'ui/Checkbox/Checkbox';
import { cexConfigs, ECexName } from 'web3';
import { dexConfigs, type IDexName } from 'web3/dexs';

import type { DisplayMode, EntityTypeFilter } from '../types';

const EXCHANGE_FILTERS_STORAGE_KEY = 'mm-dashboard:pair-search:exchange-filters';

// CEX exchanges list - must match src/web3/cexs.ts ECexName enum
const CEX_EXCHANGES: { id: ECexName; name: string }[] = [
  { id: ECexName.binance, name: 'Binance' },
  { id: ECexName.bybit, name: 'Bybit' },
  { id: ECexName.mexc, name: 'MEXC' },
  { id: ECexName.kucoin, name: 'KuCoin' },
  { id: ECexName.okx, name: 'OKX' },
  { id: ECexName.gate, name: 'Gate.io' },
  { id: ECexName.htx, name: 'HTX' },
  { id: ECexName.whitebit, name: 'WhiteBIT' },
  { id: ECexName.kraken, name: 'Kraken' },
  { id: ECexName.bitget, name: 'Bitget' },
  { id: ECexName.bitmart, name: 'BitMart' },
  { id: ECexName.lbank, name: 'LBank' },
  { id: ECexName.bingx, name: 'BingX' },
  { id: ECexName.coinstore, name: 'Coinstore' },
  { id: ECexName.hotcoin, name: 'Hotcoin' },
  { id: ECexName.indodax, name: 'Indodax' },
  { id: ECexName.xt, name: 'XT' },
];

// DEX protocols list - ALL variants from src/web3/dexs.ts IDexName type (excluding Solana)
const DEX_PROTOCOLS: { id: IDexName; name: string }[] = [
  { id: 'uniswap_v2', name: 'Uniswap V2' },
  { id: 'uniswap_v2:base', name: 'Uniswap V2 (Base)' },
  { id: 'uniswap_v3', name: 'Uniswap V3' },
  { id: 'uniswap_v3:polygon', name: 'Uniswap V3 (Polygon)' },
  { id: 'uniswap_v3:arbitrum_one', name: 'Uniswap V3 (Arbitrum)' },
  { id: 'uniswap_v3:base', name: 'Uniswap V3 (Base)' },
  { id: 'pancakeswap_v2', name: 'PancakeSwap V2' },
  { id: 'pancakeswap_v3:bsc', name: 'PancakeSwap V3' },
  { id: 'pancakeswap_inf_cl:bsc', name: 'PancakeSwap Inf CL' },
  { id: 'quickswap_v2', name: 'QuickSwap V2' },
  { id: 'sushiswap_v2', name: 'SushiSwap V2' },
  { id: 'sushiswap_v3:haqq', name: 'SushiSwap V3 (HAQQ)' },
  { id: 'camelot_v2', name: 'Camelot V2' },
  { id: 'swopfi_v2:unit0', name: 'SwopFi V2' },
  { id: 'astradex_v2:airdao', name: 'AstraDEX V2' },
  { id: 'aerodrome_v3:base', name: 'Aerodrome V3' },
  { id: 'aerodrome_v3_2:base', name: 'Aerodrome Slipstream V2' },
  { id: 'aerodrome_v2_volatile:base', name: 'Aerodrome V2' },
];

// Solana DEXes - separate from other chains
const SOLANA_DEXES: { id: IDexName; name: string }[] = [
  { id: 'jupiter:solana', name: 'Jupiter' },
  { id: 'raydium', name: 'Raydium' },
];

interface ExchangeFilters {
  cex: string[];
  dex: string[];
  sol: string[];
}

function loadExchangeFilters(): ExchangeFilters {
  try {
    const stored = localStorage.getItem(EXCHANGE_FILTERS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && typeof parsed === 'object') {
        return {
          cex: Array.isArray(parsed.cex) ? parsed.cex : [],
          dex: Array.isArray(parsed.dex) ? parsed.dex : [],
          sol: Array.isArray(parsed.sol) ? parsed.sol : [],
        };
      }
    }
  } catch {
    // Ignore parse errors
  }
  return { cex: [], dex: [], sol: [] };
}

function saveExchangeFilters(filters: ExchangeFilters): void {
  try {
    localStorage.setItem(EXCHANGE_FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // Ignore storage errors
  }
}

const SolanaIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} width="16" height="16">
    <path d="M6.5 17.5L9 15h10.5l-2.5 2.5H6.5z" />
    <path d="M6.5 6.5L9 9h10.5L17 6.5H6.5z" />
    <path d="M6.5 12l2.5-2.5h10.5L17 12H6.5z" />
  </svg>
);

const DexIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} width="14" height="14">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
  </svg>
);

interface QuickFiltersProps {
  displayMode: DisplayMode;
  onDisplayModeChange: (mode: DisplayMode) => void;
  typeFilters: EntityTypeFilter[];
  onTypeFiltersChange: (filters: EntityTypeFilter[]) => void;
  platformFilters: string[];
  onPlatformFiltersChange: (filters: string[]) => void;
  className?: string;
}

interface FilterOption<T extends string> {
  value: T;
  label: string;
  icon: React.ReactNode;
  color: string;
  activeColor: string;
  bgColor: string;
}

const displayModeOptions: FilterOption<DisplayMode>[] = [
  {
    value: 'watchlist',
    label: 'Watchlist',
    icon: <StarFilled />,
    color: 'text-orange-400',
    activeColor: 'text-orange-300',
    bgColor: 'bg-orange-500/20',
  },
  {
    value: 'active',
    label: 'Active',
    icon: <CheckCircleOutlined />,
    color: 'text-emerald-400',
    activeColor: 'text-emerald-300',
    bgColor: 'bg-emerald-500/20',
  },
  {
    value: 'archive',
    label: 'Archive',
    icon: <FolderOutlined />,
    color: 'text-amber-400',
    activeColor: 'text-amber-300',
    bgColor: 'bg-amber-500/20',
  },
];

function FilterButton<T extends string>({
  option,
  isActive,
  onClick,
}: {
  option: FilterOption<T>;
  isActive: boolean;
  onClick: () => void;
}) {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  return (
    <button
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role="switch"
      aria-checked={isActive}
      aria-label={`${option.label} filter${isActive ? ' (active)' : ''}`}
      className={cn(
        'flex flex-1 items-center justify-center gap-1.5 rounded-full border border-white/5 px-2 py-1.5 text-xs font-medium transition-colors duration-200',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500/50',
        isActive
          ? [option.bgColor, option.activeColor]
          : ['bg-white/5 hover:bg-white/8', option.color],
      )}
    >
      <span
        className={cn('text-sm', isActive ? option.activeColor : option.color)}
        aria-hidden="true"
      >
        {option.icon}
      </span>
      {option.label && <span className="hidden truncate sm:inline">{option.label}</span>}
    </button>
  );
}

interface ExchangeFilterButtonProps {
  label: string;
  icon: React.ReactNode;
  color: string;
  activeColor: string;
  bgColor: string;
  selectedExchanges: string[];
  exchanges: { id: string; name: string; icon?: string }[];
  onExchangeChange: (exchanges: string[]) => void;
}

const ExchangeFilterButton: React.FC<ExchangeFilterButtonProps> = ({
  label,
  icon,
  color,
  activeColor,
  bgColor,
  selectedExchanges,
  exchanges,
  onExchangeChange,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const hasSelectedExchanges = selectedExchanges.length > 0;
  const dropdownId = `${label.toLowerCase()}-filter-dropdown`;
  const mainButtonId = `${label.toLowerCase()}-filter-button`;
  const firstItemRef = useRef<HTMLDivElement>(null);

  // Focus first item when dropdown opens
  useEffect(() => {
    if (dropdownOpen) {
      // Small delay to ensure dropdown is rendered
      const timer = setTimeout(() => {
        firstItemRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [dropdownOpen]);

  const handleExchangeToggle = useCallback(
    (exchangeId: string) => {
      if (selectedExchanges.includes(exchangeId)) {
        onExchangeChange(selectedExchanges.filter(e => e !== exchangeId));
      } else {
        onExchangeChange([...selectedExchanges, exchangeId]);
      }
    },
    [selectedExchanges, onExchangeChange],
  );

  // Main button click toggles all exchanges
  const handleMainButtonClick = useCallback(() => {
    if (hasSelectedExchanges) {
      onExchangeChange([]);
    } else {
      onExchangeChange(exchanges.map(e => e.id));
    }
  }, [hasSelectedExchanges, exchanges, onExchangeChange]);

  // Keyboard handler for main button
  const handleMainButtonKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleMainButtonClick();
      }
    },
    [handleMainButtonClick],
  );

  // Keyboard handler for dropdown trigger
  const handleDropdownKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && dropdownOpen) {
        e.preventDefault();
        setDropdownOpen(false);
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setDropdownOpen(prev => !prev);
      } else if (e.key === 'ArrowDown' && !dropdownOpen) {
        e.preventDefault();
        setDropdownOpen(true);
      }
    },
    [dropdownOpen],
  );

  const dropdownContent = (
    <div
      id={dropdownId}
      role="group"
      aria-label={`Select ${label} exchanges`}
      className="rounded-xl border border-white/10 bg-[rgba(24,24,28,0.98)] p-3 shadow-2xl backdrop-blur-xl"
      style={{ minWidth: 200 }}
    >
      <div
        className="flex max-h-[280px] flex-col gap-1 overflow-y-auto"
        onKeyDown={e => {
          if (e.key === 'Escape') {
            setDropdownOpen(false);
          }
        }}
      >
        {exchanges.map((exchange, index) => (
          <div
            key={exchange.id}
            ref={index === 0 ? firstItemRef : undefined}
            tabIndex={0}
            role="option"
            aria-selected={selectedExchanges.includes(exchange.id)}
            onClick={() => handleExchangeToggle(exchange.id)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleExchangeToggle(exchange.id);
              } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                const next = e.currentTarget.nextElementSibling as HTMLElement;
                next?.focus();
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                const prev = e.currentTarget.previousElementSibling as HTMLElement;
                prev?.focus();
              } else if (e.key === 'Home') {
                e.preventDefault();
                const first = e.currentTarget.parentElement?.firstElementChild as HTMLElement;
                first?.focus();
              } else if (e.key === 'End') {
                e.preventDefault();
                const last = e.currentTarget.parentElement?.lastElementChild as HTMLElement;
                last?.focus();
              }
            }}
            className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-white/5 focus:bg-white/10 focus:outline-none"
          >
            <Checkbox
              checked={selectedExchanges.includes(exchange.id)}
              onChange={() => handleExchangeToggle(exchange.id)}
            />
            {exchange.icon && (
              <img
                src={exchange.icon}
                alt=""
                aria-hidden="true"
                className="h-5 w-5 shrink-0 rounded object-contain"
              />
            )}
            <span className="text-sm text-gray-200">{exchange.name}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    // Fixed width container to prevent any layout shift
    <div className="relative flex" role="group" aria-label={`${label} filter`}>
      <button
        id={mainButtonId}
        onClick={handleMainButtonClick}
        onKeyDown={handleMainButtonKeyDown}
        aria-pressed={hasSelectedExchanges}
        aria-label={
          hasSelectedExchanges
            ? `Clear all ${label} filters (${selectedExchanges.length} selected)`
            : `Select all ${label} filters`
        }
        className={cn(
          'flex items-center gap-1.5 rounded-l-full border-y border-l border-white/5 px-2 py-1.5 text-xs font-medium transition-colors duration-200',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500/50',
          hasSelectedExchanges ? [bgColor, activeColor] : ['bg-white/5 hover:bg-white/8', color],
        )}
      >
        <span
          className={cn('text-sm', hasSelectedExchanges ? activeColor : color)}
          aria-hidden="true"
        >
          {icon}
        </span>
        <span className="hidden sm:inline">{label}</span>
        {/* Always reserve space for counter to prevent layout shift */}
        <span
          className={cn(
            'flex h-4 w-5 items-center justify-center rounded-full text-[10px] font-bold',
            hasSelectedExchanges ? 'bg-white/20' : 'invisible',
          )}
          aria-hidden={!hasSelectedExchanges}
        >
          {selectedExchanges.length || '0'}
        </span>
      </button>
      <Dropdown
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        trigger={['click']}
        popupRender={() => dropdownContent}
        placement="bottomRight"
      >
        <button
          aria-haspopup="listbox"
          aria-expanded={dropdownOpen}
          aria-controls={dropdownId}
          aria-label={`Open ${label} filter options`}
          onKeyDown={handleDropdownKeyDown}
          className={cn(
            'flex items-center justify-center rounded-r-full border-y border-r border-white/5 px-1.5 py-1.5 text-xs transition-colors duration-200',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500/50',
            hasSelectedExchanges ? [bgColor, activeColor] : ['bg-white/5 hover:bg-white/8', color],
          )}
          onClick={e => {
            e.stopPropagation();
            setDropdownOpen(!dropdownOpen);
          }}
        >
          <DownOutlined
            aria-hidden="true"
            className={cn(
              'text-[8px] transition-transform',
              dropdownOpen && 'rotate-180',
              hasSelectedExchanges ? activeColor : color,
            )}
          />
        </button>
      </Dropdown>
    </div>
  );
};

export const QuickFilters: React.FC<QuickFiltersProps> = ({
  displayMode,
  onDisplayModeChange,
  typeFilters,
  onTypeFiltersChange,
  platformFilters,
  onPlatformFiltersChange,
  className,
}) => {
  // Load persisted exchange filters
  const [exchangeFilters, setExchangeFilters] = useState<ExchangeFilters>(loadExchangeFilters);

  // Prepare CEX exchanges with icons
  const cexExchangesWithIcons = useMemo(
    () =>
      CEX_EXCHANGES.map(ex => ({
        id: ex.id,
        name: ex.name,
        icon: cexConfigs[ex.id]?.icon,
      })),
    [],
  );

  // Prepare DEX protocols with icons
  const dexProtocolsWithIcons = useMemo(
    () =>
      DEX_PROTOCOLS.map(ex => ({
        id: ex.id,
        name: ex.name,
        icon: dexConfigs[ex.id]?.icon,
      })),
    [],
  );

  // Prepare Solana DEXes with icons
  const solanaDexesWithIcons = useMemo(
    () =>
      SOLANA_DEXES.map(ex => ({
        id: ex.id,
        name: ex.name,
        icon: dexConfigs[ex.id]?.icon,
      })),
    [],
  );

  // Persist exchange filters
  useEffect(() => {
    saveExchangeFilters(exchangeFilters);
  }, [exchangeFilters]);

  // Sync exchange filters with platform filters
  useEffect(() => {
    const allSelected = [...exchangeFilters.cex, ...exchangeFilters.dex, ...exchangeFilters.sol];
    if (JSON.stringify(allSelected.sort()) !== JSON.stringify([...platformFilters].sort())) {
      onPlatformFiltersChange(allSelected);
    }
  }, [exchangeFilters, onPlatformFiltersChange, platformFilters]);

  const handleTypeFilterToggle = useCallback(
    (filter: EntityTypeFilter) => {
      if (typeFilters.includes(filter)) {
        onTypeFiltersChange(typeFilters.filter(f => f !== filter));
      } else {
        onTypeFiltersChange([...typeFilters, filter]);
      }
    },
    [typeFilters, onTypeFiltersChange],
  );

  const handleCexExchangeChange = useCallback((exchanges: string[]) => {
    setExchangeFilters(prev => ({ ...prev, cex: exchanges }));
  }, []);

  const handleDexExchangeChange = useCallback((exchanges: string[]) => {
    setExchangeFilters(prev => ({ ...prev, dex: exchanges }));
  }, []);

  const handleSolExchangeChange = useCallback((exchanges: string[]) => {
    setExchangeFilters(prev => ({ ...prev, sol: exchanges }));
  }, []);

  // Other type filter options (without CEX/DEX/SOL which have dropdowns)
  const otherTypeFilterOptions: FilterOption<EntityTypeFilter>[] = useMemo(
    () => [
      {
        value: 'arbitrage',
        label: 'Arbitrage',
        icon: <SwapOutlined />,
        color: 'text-pink-400',
        activeColor: 'text-pink-300',
        bgColor: 'bg-pink-500/20',
      },
      {
        value: 'balancer',
        label: 'Balancer',
        icon: <WalletOutlined />,
        color: 'text-cyan-400',
        activeColor: 'text-cyan-300',
        bgColor: 'bg-cyan-500/20',
      },
    ],
    [],
  );

  return (
    <nav className={cn('flex flex-col gap-2', className)} aria-label="Search filters">
      {/* Display mode - radio group behavior (single select) */}
      <div className="flex gap-1.5" role="radiogroup" aria-label="Display mode">
        {displayModeOptions.map(option => (
          <button
            key={option.value}
            onClick={() => onDisplayModeChange(option.value)}
            role="radio"
            aria-checked={displayMode === option.value}
            aria-label={option.label}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 rounded-full border border-white/5 px-2 py-1.5 text-xs font-medium transition-colors duration-200',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500/50',
              displayMode === option.value
                ? [option.bgColor, option.activeColor]
                : ['bg-white/5 hover:bg-white/8', option.color],
            )}
          >
            <span
              className={cn(
                'text-sm',
                displayMode === option.value ? option.activeColor : option.color,
              )}
              aria-hidden="true"
            >
              {option.icon}
            </span>
            {option.label && <span className="hidden truncate sm:inline">{option.label}</span>}
          </button>
        ))}
      </div>
      {/* Entity type filters - toolbar with multiple controls */}
      <div className="flex gap-1.5" role="toolbar" aria-label="Entity type filters">
        {/* CEX with dropdown */}
        <ExchangeFilterButton
          label="CEX"
          icon={<StockOutlined />}
          color="text-blue-400"
          activeColor="text-blue-300"
          bgColor="bg-blue-500/20"
          selectedExchanges={exchangeFilters.cex}
          exchanges={cexExchangesWithIcons}
          onExchangeChange={handleCexExchangeChange}
        />
        {/* DEX with dropdown */}
        <ExchangeFilterButton
          label="DEX"
          icon={<DexIcon className="text-teal-400" />}
          color="text-teal-400"
          activeColor="text-teal-300"
          bgColor="bg-teal-500/20"
          selectedExchanges={exchangeFilters.dex}
          exchanges={dexProtocolsWithIcons}
          onExchangeChange={handleDexExchangeChange}
        />
        {/* SOL with dropdown */}
        <ExchangeFilterButton
          label="SOL"
          icon={<SolanaIcon className="text-violet-400" />}
          color="text-violet-400"
          activeColor="text-violet-300"
          bgColor="bg-violet-500/20"
          selectedExchanges={exchangeFilters.sol}
          exchanges={solanaDexesWithIcons}
          onExchangeChange={handleSolExchangeChange}
        />
        {/* Other filters */}
        {otherTypeFilterOptions.map(option => (
          <FilterButton
            key={option.value}
            option={option}
            isActive={typeFilters.includes(option.value)}
            onClick={() => handleTypeFilterToggle(option.value)}
          />
        ))}
      </div>
    </nav>
  );
};
